/**
 * The complete outside contact of the booking widget.
 *
 * Everything the flow knows arrives through `BookingBridge`. There is no other network call,
 * no other source of truth, and no local derivation of anything commercial. Swapping the mock
 * implementation for the real HTTP one must not require a change in `src/flow` — if it does,
 * something leaked out of this file.
 *
 * Shape follows the canonical booking widget masterplan: §5.3 (public projection),
 * §6.2 (option type), §8.2 (routes) and §8.4 (typed commands).
 */

// ─────────────────────────────────────────────────────────────────────────────
// Money
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A server-rendered amount.
 *
 * Deliberately carries no numeric field. The browser must never add a room price to a tax to
 * an add-on — masterplan rule 1 — and the cheapest way to guarantee that is to make the
 * arithmetic impossible to write rather than forbidden by review. Ordering and comparison are
 * the server's job too: lists arrive in the order they should be shown.
 */
export type Money = {
  readonly formatted: string;
  readonly currency: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// Options — §6.2
// ─────────────────────────────────────────────────────────────────────────────

export const OPTION_SCOPES = ["room", "booking", "guest"] as const;
export type OptionScope = (typeof OPTION_SCOPES)[number];

export const OPTION_KINDS = [
  "inventory_requirement",
  "preference",
  "addon",
  "rate_benefit",
] as const;
export type OptionKind = (typeof OPTION_KINDS)[number];

/**
 * What the hotel is actually promising. A sea view sold as a category is `guaranteed`; a sea
 * view that only some rooms of the category have is `requested`. Never inferred client-side.
 */
export const GUARANTEE_LEVELS = ["guaranteed", "requested", "requires_review"] as const;
export type GuaranteeLevel = (typeof GUARANTEE_LEVELS)[number];

export const PRICE_STATUSES = ["included", "priced", "price_on_request", "free"] as const;
export type PriceStatus = (typeof PRICE_STATUSES)[number];

export const AVAILABILITY_STATUSES = ["available", "limited", "unavailable", "unknown"] as const;
export type AvailabilityStatus = (typeof AVAILABILITY_STATUSES)[number];

export const PRICING_UNITS = [
  "per_stay",
  "per_night",
  "per_person",
  "per_person_per_night",
  "per_room",
  "per_room_per_night",
  "per_item",
] as const;
export type PricingUnit = (typeof PRICING_UNITS)[number];

/**
 * A bookable option as the hotel publishes it.
 *
 * `guaranteeLevel` and `priceStatus` are required, so an option that does not state what it
 * promises and what it costs cannot be constructed at all. That is the type-level form of
 * masterplan rule 6 — no silent guarantees.
 */
export type BookingOption = {
  readonly code: string;
  readonly displayName: string;
  readonly description: string | null;
  readonly scope: OptionScope;
  readonly kind: OptionKind;
  readonly guaranteeLevel: GuaranteeLevel;
  readonly priceStatus: PriceStatus;
  readonly availabilityStatus: AvailabilityStatus;
  /** Shown next to the option. Absent when `priceStatus` is `included` or `free`. */
  readonly price: Money | null;
  readonly pricingUnit: PricingUnit | null;
  readonly conflictsWith: readonly string[];
  readonly requires: readonly string[];
  readonly maxQuantity: number | null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Bootstrap — what this hotel publishes
// ─────────────────────────────────────────────────────────────────────────────

export const THEME_MODES = ["light", "dark", "system"] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

/**
 * Mirrors the fields that already exist on `HotelGuestSettings` in the backend
 * (`guest_app_accent_color`, `guest_app_theme_mode`, `guest_app_logo_url`,
 * `guest_app_hero_image_url`) so wiring this up later needs no new configuration surface.
 */
export type HotelBranding = {
  readonly accentColor: string | null;
  readonly themeMode: ThemeMode;
  readonly logoUrl: string | null;
  readonly heroImageUrl: string | null;
};

export type HotelPresentation = {
  readonly hotelId: string;
  readonly name: string;
  readonly city: string | null;
  readonly countryCode: string | null;
  readonly branding: HotelBranding;
};

export type RoomCategoryOption = {
  readonly code: string;
  readonly displayName: string;
  readonly description: string | null;
  readonly maxAdults: number;
  readonly maxOccupancy: number;
  readonly imageUrl: string | null;
  /** Attributes the category itself guarantees, e.g. a sea view sold as a category. */
  readonly guaranteedAttributes: readonly BookingOption[];
};

export type RateProductOption = {
  readonly code: string;
  readonly displayName: string;
  readonly description: string | null;
  /** Canonical meal plan code as persisted by the backend — `EX` is Room Only. */
  readonly mealPlanCode: string;
  /** What the guest sees. The backend keeps `EX`; the guest reads "Room Only". */
  readonly mealPlanLabel: string;
  readonly includedBenefits: readonly string[];
  readonly cancellationSummary: string | null;
  readonly paymentSummary: string | null;
};

/**
 * Thresholds the hotel publishes so the widget can stay quiet instead of guessing.
 *
 * Without `parentBedMaxAge` the widget would have to decide for itself when a child needs a
 * bed. It must not: a teenager needs a real or extra bed, only an infant in the parent bed
 * does not count against capacity, and where that line sits is the hotel's rule.
 */
export type PublishedRules = {
  /** Highest age that may sleep in the parent bed without consuming capacity. `null` disables the option. */
  readonly parentBedMaxAge: number | null;
  /** Age from which a guest counts as an adult for occupancy. */
  readonly adultFromAge: number;
  /** Ages must be collected because they change price or capacity. */
  readonly requiresMinorAges: boolean;
  readonly maxRooms: number;
  readonly maxNights: number;
  readonly maxAdultsPerRoom: number;
};

export type HotelBootstrap = {
  readonly hotel: HotelPresentation;
  readonly rules: PublishedRules;
  readonly categories: readonly RoomCategoryOption[];
  readonly rates: readonly RateProductOption[];
  /** Non-binding room wishes, e.g. high floor or quiet. Always `requested`. */
  readonly preferences: readonly BookingOption[];
  /** Paid extras with an active catalogue entry and a resolvable price. */
  readonly addons: readonly BookingOption[];
  /** Hard requirements such as accessibility, sold as guaranteed. */
  readonly requirements: readonly BookingOption[];
  readonly languages: readonly string[];
  readonly defaultLanguage: string;
  readonly currency: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// Projection — §5.3
// ─────────────────────────────────────────────────────────────────────────────

export const BOOKING_STATUSES = [
  "collecting",
  "availability_ready",
  "quote_ready",
  "checkout_ready",
  "payment_pending",
  "payment_authorized",
  "paid",
  "reservation_committing",
  "booked",
  "needs_clarification",
  "manual_review_required",
  "quote_expired",
  "payment_failed",
  "payment_expired",
  "commit_retry_required",
  "cancelled",
  "superseded",
] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

export const BOOKING_CHANNELS = ["widget", "mail", "dashboard", "guest_app"] as const;
export type BookingChannel = (typeof BOOKING_CHANNELS)[number];

export type StayProjection = {
  readonly checkin: string;
  readonly checkout: string;
  readonly nights: number;
};

export type MinorGuest = {
  readonly id: string;
  readonly age: number | null;
  /** Server-confirmed, never a client guess: does this child consume a bed. */
  readonly sleepsInParentBed: boolean;
  readonly countsAgainstCapacity: boolean;
};

/**
 * Whether one category can take *this* room's party.
 *
 * Per room rather than per flow: two rooms in the same booking can have different parties, so a
 * family suite that works for one is wrong for the other. Resolved by the server — the widget
 * must not re-derive capacity, which is exactly the kind of rule that drifts.
 */
export type RoomCategoryAvailability = {
  readonly code: string;
  readonly selectable: boolean;
  /** Stated at the point of choosing, not as an error after the click. */
  readonly reason: string | null;
};

export type RoomProjection = {
  readonly roomId: string;
  readonly adults: number;
  readonly minors: readonly MinorGuest[];
  readonly categoryCode: string | null;
  readonly rateProductCode: string | null;
  /** Hard requirements the guest selected for this room. */
  readonly requirements: readonly string[];
  /** Non-binding wishes for this room. */
  readonly preferences: readonly string[];
  readonly availabilityStatus: AvailabilityStatus;
  readonly categoryOptions: readonly RoomCategoryAvailability[];
  /** Server-rendered one-liner for the summary, e.g. "2 Erwachsene · 1 Kind (3)". */
  readonly partyLabel: string;
};

export type SelectedAddon = {
  readonly addonId: string;
  readonly code: string;
  readonly displayName: string;
  readonly scope: OptionScope;
  readonly roomId: string | null;
  readonly quantity: number;
  readonly fromDate: string | null;
  readonly toDate: string | null;
  readonly guaranteeLevel: GuaranteeLevel;
  readonly priceStatus: PriceStatus;
};

export type ContactProjection = {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly isBusinessTrip: boolean;
  readonly companyName: string | null;
};

export type AvailabilityCategory = {
  readonly code: string;
  readonly status: AvailabilityStatus;
  readonly remainingHint: string | null;
  /**
   * Lowest nightly price for this category and this stay, already rendered.
   *
   * Without it the category step asks the guest to choose between rooms with no idea what any
   * of them costs, and they pick blind or leave. Computed server-side like every other amount —
   * the client only prints it.
   */
  readonly fromPrice: Money | null;
};

export type AvailabilityProjection = {
  readonly status: AvailabilityStatus;
  readonly resolvedAt: string;
  /** Categories for this stay, in the order to display. */
  readonly categories: readonly AvailabilityCategory[];
  /**
   * Set when the server made a choice on the guest's behalf because only one option qualified.
   * Shown to them — a silent selection is indistinguishable from a bug.
   */
  readonly autoSelectionNote: string | null;
};

export type QuoteRoomLine = {
  readonly roomId: string;
  readonly categoryName: string;
  readonly rateName: string;
  readonly mealPlanLabel: string;
  readonly nights: readonly { readonly date: string; readonly price: Money }[];
  readonly includedBenefits: readonly string[];
  readonly subtotal: Money;
};

export type QuoteAddonLine = {
  readonly addonId: string;
  readonly displayName: string;
  readonly quantityLabel: string;
  readonly total: Money;
};

export type QuoteProjection = {
  readonly quoteId: string;
  readonly version: number;
  readonly fingerprint: string;
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly roomLines: readonly QuoteRoomLine[];
  readonly addonLines: readonly QuoteAddonLine[];
  /** City tax and VAT as the canonical policy path projected them. Never recomputed here. */
  readonly taxLines: readonly { readonly label: string; readonly total: Money; readonly note: string | null }[];
  readonly total: Money;
  readonly dueNow: Money;
  readonly dueLater: Money;
  readonly paymentSummary: string;
  readonly cancellationSummary: string;
};

export type PaymentProjection = {
  readonly state: string;
  readonly dueNow: Money | null;
  readonly provider: string | null;
};

export const BLOCKING_SEVERITIES = ["blocking", "warning"] as const;
export type BlockingSeverity = (typeof BLOCKING_SEVERITIES)[number];

/**
 * Anchored to the field that caused it so the flow can render it in place. A collected error
 * list at the end of a booking funnel is where guests give up.
 */
export type BlockingReason = {
  readonly code: string;
  readonly message: string;
  readonly severity: BlockingSeverity;
  readonly step: FlowStepId | null;
  readonly roomId: string | null;
  readonly field: string | null;
};

export const BOOKING_ACTIONS = [
  "set_stay",
  "set_contact",
  "set_business_trip",
  "add_room",
  "remove_room",
  "update_room_party",
  "set_room_category",
  "set_room_rate",
  "set_room_requirement",
  "set_room_preference",
  "add_addon",
  "update_addon",
  "remove_addon",
  "request_quote",
  "accept_quote",
  "create_checkout",
] as const;
export type BookingActionName = (typeof BOOKING_ACTIONS)[number];

export const FLOW_STEPS = [
  "stay",
  "rooms",
  "party",
  "category",
  "rate",
  "preferences",
  "extras",
  "contact",
  "review",
  "payment",
  "status",
] as const;
export type FlowStepId = (typeof FLOW_STEPS)[number];

export type BookingProjection = {
  readonly flowId: string;
  readonly revision: number;
  readonly status: BookingStatus;
  readonly channel: BookingChannel;
  readonly hotel: HotelPresentation;
  readonly stay: StayProjection | null;
  readonly rooms: readonly RoomProjection[];
  readonly addons: readonly SelectedAddon[];
  readonly contact: ContactProjection | null;
  readonly availability: AvailabilityProjection | null;
  readonly quote: QuoteProjection | null;
  readonly payment: PaymentProjection | null;
  readonly blockingReasons: readonly BlockingReason[];
  /** What the server will accept next. The flow renders from this, never from its own guess. */
  readonly allowedActions: readonly BookingActionName[];
  readonly expiresAt: string | null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Commands — §8.4
// ─────────────────────────────────────────────────────────────────────────────

export type MinorGuestInput = {
  readonly age: number | null;
  readonly sleepsInParentBed: boolean;
};

export type BookingCommand =
  | { readonly type: "set_stay"; readonly checkin: string; readonly checkout: string }
  | {
      readonly type: "set_contact";
      readonly firstName: string;
      readonly lastName: string;
      readonly email: string;
      readonly phone: string | null;
    }
  | { readonly type: "set_business_trip"; readonly isBusinessTrip: boolean; readonly companyName: string | null }
  | { readonly type: "add_room" }
  | { readonly type: "remove_room"; readonly roomId: string }
  | {
      readonly type: "update_room_party";
      readonly roomId: string;
      readonly adults: number;
      readonly minors: readonly MinorGuestInput[];
    }
  | { readonly type: "set_room_category"; readonly roomId: string; readonly categoryCode: string }
  | { readonly type: "set_room_rate"; readonly roomId: string; readonly rateProductCode: string }
  | {
      readonly type: "set_room_requirement";
      readonly roomId: string;
      readonly optionCode: string;
      readonly selected: boolean;
    }
  | {
      readonly type: "set_room_preference";
      readonly roomId: string;
      readonly optionCode: string;
      readonly selected: boolean;
    }
  | {
      readonly type: "add_addon";
      readonly code: string;
      readonly roomId: string | null;
      readonly quantity: number;
      readonly fromDate: string | null;
      readonly toDate: string | null;
    }
  | { readonly type: "update_addon"; readonly addonId: string; readonly quantity: number }
  | { readonly type: "remove_addon"; readonly addonId: string }
  | { readonly type: "accept_quote"; readonly quoteId: string };

// ─────────────────────────────────────────────────────────────────────────────
// Results
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A stale write. Carries the current projection so the flow can show what moved instead of
 * discarding the guest's work — and never silently reprices.
 */
export type RevisionConflict = {
  readonly kind: "revision_conflict";
  readonly code: "REVISION_CONFLICT" | "QUOTE_EXPIRED";
  readonly current: BookingProjection;
};

export type CommandResult = { readonly kind: "ok"; readonly projection: BookingProjection } | RevisionConflict;

export type CheckoutSession = {
  readonly checkoutSessionId: string;
  readonly clientSecret: string;
  readonly publishableKey: string;
  readonly mode: "sandbox" | "live";
  readonly quoteFingerprint: string;
  readonly expiresAt: string;
};

export type BookingStatusReport = {
  readonly flowId: string;
  readonly status: BookingStatus;
  readonly reservationIds: readonly string[];
  readonly message: string | null;
};

export type CreateFlowInput = {
  readonly language: string;
  readonly channel: BookingChannel;
  /** Present when the guest arrived from a mail continuation link. */
  readonly continuationToken?: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// The bridge
// ─────────────────────────────────────────────────────────────────────────────

export const BRIDGE_ERROR_CODES = [
  "BRIDGE_NOT_WIRED",
  "SESSION_REJECTED",
  "FLOW_NOT_FOUND",
  "TENANT_OR_ORIGIN_MISMATCH",
  "BOOKING_REQUIREMENT_UNSATISFIED",
  "RATE_LIMITED",
  "UNAVAILABLE",
] as const;
export type BridgeErrorCode = (typeof BRIDGE_ERROR_CODES)[number];

export class BridgeError extends Error {
  readonly code: BridgeErrorCode;
  readonly retryable: boolean;

  constructor(code: BridgeErrorCode, message?: string, retryable = false) {
    super(message ?? code);
    this.name = "BridgeError";
    this.code = code;
    this.retryable = retryable;
  }
}

export interface BookingBridge {
  bootstrap(publicKey: string, launchOrigin?: string): Promise<HotelBootstrap>;
  createFlow(input: CreateFlowInput): Promise<BookingProjection>;
  getFlow(flowId: string): Promise<BookingProjection>;
  applyCommand(
    flowId: string,
    revision: number,
    command: BookingCommand,
    idempotencyKey: string,
  ): Promise<CommandResult>;
  requestQuote(flowId: string, revision: number): Promise<CommandResult>;
  createCheckoutSession(flowId: string, quoteId: string): Promise<CheckoutSession>;
  getStatus(flowId: string): Promise<BookingStatusReport>;
}
